"use client";

import { type ReactNode } from "react";
import { Box, Button, Dialog, Flex, Portal, Stack, Text } from "@chakra-ui/react";
import { LuX, LuTriangleAlert, LuInfo, LuCircleAlert } from "react-icons/lu";

export type ConfirmationVariant = "danger" | "warning" | "info";

interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Callback when the confirm button is clicked */
  onConfirm: () => void | Promise<void>;
  /** Dialog title */
  title: string;
  /** Dialog description/body content */
  description?: ReactNode;
  /** Text for the confirm button */
  confirmLabel?: string;
  /** Text for the cancel button */
  cancelLabel?: string;
  /** Visual variant that affects colors */
  variant?: ConfirmationVariant;
  /** Whether the confirm action is in progress */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether to show an icon in the header */
  showIcon?: boolean;
}

const VARIANT_CONFIG: Record<
  ConfirmationVariant,
  {
    icon: typeof LuTriangleAlert;
    iconBg: string;
    iconColor: string;
    buttonBg: string;
    buttonHoverBg: string;
  }
> = {
  danger: {
    icon: LuTriangleAlert,
    iconBg: "rgba(239, 68, 68, 0.1)",
    iconColor: "var(--color-error)",
    buttonBg: "var(--color-error)",
    buttonHoverBg: "#DC2626",
  },
  warning: {
    icon: LuCircleAlert,
    iconBg: "rgba(245, 158, 11, 0.1)",
    iconColor: "var(--color-warning)",
    buttonBg: "var(--color-warning)",
    buttonHoverBg: "#D97706",
  },
  info: {
    icon: LuInfo,
    iconBg: "rgba(0, 102, 204, 0.08)",
    iconColor: "var(--color-primary)",
    buttonBg: "var(--color-primary)",
    buttonHoverBg: "var(--color-primary-hover)",
  },
};

/**
 * A reusable confirmation dialog for destructive or important actions.
 * Supports danger, warning, and info variants.
 */
export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  loading = false,
  error = null,
  showIcon = true,
}: ConfirmationDialogProps) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop
          bg="rgba(0, 0, 0, 0.4)"
          backdropFilter="blur(4px)"
          transition="all 0.2s ease"
        />
        <Dialog.Positioner
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="fixed"
          inset={0}
        >
          <Dialog.Content
            maxW={{ base: "calc(100vw - 32px)", md: "420px" }}
            w="full"
            bg="white"
            borderRadius="12px"
            border="0.5px solid var(--color-border)"
            boxShadow="0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)"
            overflow="hidden"
            p={0}
          >
            {/* Header */}
            <Box px={6} pt={6} pb={0}>
              <Flex justify="space-between" align="flex-start">
                <Flex gap={4} align="flex-start">
                  {showIcon && (
                    <Box
                      w="44px"
                      h="44px"
                      borderRadius="10px"
                      bg={config.iconBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <Icon size={22} color={config.iconColor} />
                    </Box>
                  )}
                  <Box pt={showIcon ? 1 : 0}>
                    <Text
                      fontSize="17px"
                      fontWeight="600"
                      color="var(--color-text-primary)"
                      letterSpacing="-0.01em"
                      lineHeight="1.3"
                    >
                      {title}
                    </Text>
                  </Box>
                </Flex>
                <Box
                  as="button"
                  onClick={onClose}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  color="var(--color-text-tertiary)"
                  transition="all 0.15s ease"
                  ml={3}
                  flexShrink={0}
                  _hover={{
                    bg: "var(--color-surface)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  <LuX size={18} />
                </Box>
              </Flex>
            </Box>

            {/* Body */}
            <Box px={6} py={4} pl={showIcon ? "74px" : 6}>
              <Stack gap={3}>
                {error && (
                  <Box
                    p={3}
                    bg="rgba(239, 68, 68, 0.08)"
                    border="0.5px solid rgba(239, 68, 68, 0.2)"
                    borderRadius="8px"
                  >
                    <Text fontSize="14px" color="rgb(220, 38, 38)">
                      {error}
                    </Text>
                  </Box>
                )}

                {description && (
                  <Text
                    fontSize="14px"
                    color="var(--color-text-secondary)"
                    lineHeight="1.6"
                  >
                    {description}
                  </Text>
                )}
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              px={6}
              py={4}
              bg="var(--color-surface)"
              borderTop="0.5px solid var(--color-border)"
            >
              <Flex
                gap={3}
                justify="flex-end"
                flexDirection={{ base: "column-reverse", sm: "row" }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="ghost"
                  h="40px"
                  px={4}
                  fontSize="14px"
                  fontWeight="500"
                  color="var(--color-text-secondary)"
                  borderRadius="8px"
                  transition="all 0.15s ease"
                  w={{ base: "full", sm: "auto" }}
                  _hover={{
                    bg: "white",
                    color: "var(--color-text-primary)",
                  }}
                >
                  {cancelLabel}
                </Button>
                <Button
                  onClick={handleConfirm}
                  loading={loading}
                  disabled={loading}
                  bg={config.buttonBg}
                  color="white"
                  h="40px"
                  px={5}
                  fontSize="14px"
                  fontWeight="500"
                  borderRadius="8px"
                  transition="all 0.15s ease"
                  w={{ base: "full", sm: "auto" }}
                  _hover={{
                    bg: config.buttonHoverBg,
                  }}
                  _active={{
                    transform: "scale(0.98)",
                  }}
                >
                  {confirmLabel}
                </Button>
              </Flex>
            </Box>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
